import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, RefreshCw, Download, ImageIcon, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * TrackPosterModal — IG-story-sized (1080×1920) share poster for a track.
 *
 * On open: GET /tracks/{id}/poster (auto-generates on first hit).
 * Regenerate button: POST /tracks/{id}/poster/regenerate — cycles through
 * six template styles so the artist can pick a look they like.
 * Download button: pulls the PNG and saves as `<title>-poster.png`.
 */
export default function TrackPosterModal({ track, onClose }) {
  const [poster, setPoster] = useState(null); // { poster_path, poster_style, poster_style_index }
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/tracks/${track.id}/poster`)
      .then((r) => { if (!cancelled) setPoster(r.data); })
      .catch(() => toast.error("Couldn't load poster"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [track.id]);

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/tracks/${track.id}/poster/regenerate`);
      setPoster(data);
      toast.success(`New style: ${data.poster_style.replace(/-/g, " ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const download = async () => {
    if (!poster?.poster_path) return;
    try {
      const url = fileUrl(poster.poster_path);
      const res = await fetch(url);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${(track.title || "poster").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-poster.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error("Download failed");
    }
  };

  const copyLink = async () => {
    if (!poster?.poster_path) return;
    try {
      await navigator.clipboard.writeText(fileUrl(poster.poster_path));
      toast.success("Poster URL copied");
    } catch {
      toast.error("Couldn't copy — download instead");
    }
  };

  return (
    <div
      data-testid="track-poster-modal"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-[#0A0A0A] border border-[#2A2A2A] max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#2A2A2A] p-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 grid place-items-center bg-[#FF4500]/10 border border-[#FF4500]/40 shrink-0">
              <ImageIcon className="h-4 w-4 text-[#FF4500]" />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF4500]">Share poster · 1080×1920</div>
              <div className="font-display font-bold text-lg md:text-xl truncate">{track.title}</div>
            </div>
          </div>
          <button
            data-testid="track-poster-close"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center hover:bg-white/5 transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6">
          <div className="grid md:grid-cols-[minmax(0,1fr)_260px] gap-6 items-start">
            {/* Preview */}
            <div
              data-testid="track-poster-preview"
              className="relative bg-black border border-[#2A2A2A] aspect-[9/16] w-full overflow-hidden grid place-items-center"
            >
              {loading || !poster ? (
                <div className="text-center">
                  <Loader2 className="h-6 w-6 text-[#FF4500] animate-spin mx-auto mb-2" />
                  <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500">Rendering…</div>
                </div>
              ) : (
                <>
                  <img
                    key={poster.poster_path}
                    src={`${fileUrl(poster.poster_path)}?t=${Date.now()}`}
                    alt={`${track.title} poster`}
                    className={`h-full w-full object-contain transition-opacity ${regenerating ? "opacity-30" : "opacity-100"}`}
                  />
                  {regenerating && (
                    <div className="absolute inset-0 grid place-items-center">
                      <Loader2 className="h-8 w-8 text-[#FF4500] animate-spin" />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Controls */}
            <div className="space-y-3">
              <div className="border border-[#2A2A2A] p-3 bg-[#121212]">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Current style</div>
                <div className="font-display font-bold text-lg mt-1 capitalize">
                  {poster?.poster_style?.replace(/-/g, " ") || "…"}
                </div>
                <div className="font-mono text-[10px] text-gray-500 mt-1">
                  {typeof poster?.poster_style_index === "number"
                    ? `Variant ${poster.poster_style_index + 1} of 6`
                    : "Loading"}
                </div>
              </div>

              <Button
                data-testid="track-poster-regenerate"
                onClick={regenerate}
                disabled={regenerating || loading}
                className="w-full rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${regenerating ? "animate-spin" : ""}`} />
                {regenerating ? "Rendering…" : "Try another style"}
              </Button>

              <Button
                data-testid="track-poster-download"
                onClick={download}
                disabled={loading || !poster}
                variant="outline"
                className="w-full rounded-none border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-11 flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" /> Download PNG
              </Button>

              <Button
                data-testid="track-poster-copy"
                onClick={copyLink}
                disabled={loading || !poster}
                variant="outline"
                className="w-full rounded-none border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-11 flex items-center justify-center gap-2"
              >
                <Copy className="h-4 w-4" /> Copy URL
              </Button>

              <p className="font-mono text-[10px] text-gray-500 leading-relaxed pt-2">
                Perfect for Instagram, TikTok &amp; Snapchat stories. Every regenerate cycles
                to a new template — six looks in total.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
